import React, { useCallback } from 'react';
import Text from 'design-system/Text';
import { StyleSheet, View } from 'react-native';
import { Button, ButtonGroup, rem, Spacer } from 'design-system';
import Squircle from 'design-system/Squircle';
import useColors from 'hooks/useColors';
import IconUnlocked from 'design-system/icons/IconUnlocked';
import links from 'utils/links';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import { benefits } from '../benefits';
import { selectDefaultFreeTrialDays } from '../selectors';
export const isMicroModalBenefitId = (benefitId) => [
    'customCategories',
    'export',
    'offlineAccounts',
    'smartRules',
    'spaces',
    'budgeting',
    'cashback',
    'rentReporting',
    'forceSync',
    'connections',
    'business',
    'split',
    'changeTransactionDate',
    'renameTransactions',
    'advancedTransactionsEditing',
].includes(benefitId);
function isPlural(subject) {
    const lower = subject.toLowerCase().trim();
    return lower.endsWith('s') && !lower.endsWith('ss');
}
function getVerbForm(subject) {
    return isPlural(subject) ? 'are' : 'is';
}
const RenderUnlockIcon = () => {
    const colors = useColors();
    const renderIcon = useCallback(() => (<Squircle width={64} height={64} color={colors.background.dark}>
        <IconUnlocked width={32} height={32}/>
      </Squircle>), [colors.background.dark]);
    return renderIcon();
};
export const UpSellCard = () => {
    const cardData = [
        {
            preHeading: 'Over',
            heading: '3M',
            subHeading: 'downloads',
        },
        {
            preHeading: 'Get',
            heading: '10+',
            subHeading: 'premium features',
        },
    ];
    const styles = useStyles(createStyleSheets((colors) => ({
        cardContainer: {
            flexDirection: 'row',
            gap: rem(8),
            marginTop: rem(8),
        },
        card: {
            flex: 1,
            padding: rem(16),
            borderRadius: rem(16),
            borderColor: colors.borders.divider,
            borderWidth: rem(1),
        },
    })));
    return (<View style={styles.cardContainer}>
      {cardData.map((cardItem, index) => (<View style={styles.card} key={index}>
          <Text Text-14 centered>
            {cardItem.preHeading}
          </Text>
          <Spacer h={4}/>
          <Text Text-20 OnBrand centered>
            {cardItem.heading}
          </Text>
          <Text TextThin-14 Secondary centered>
            {cardItem.subHeading}
          </Text>
        </View>))}
    </View>);
};
export const UpSellButtons = ({ onCancel, onContinue, }) => {
    const colors = useColors();
    const defaultFreeTrialDays = useAppSelector(selectDefaultFreeTrialDays);
    return (<View>
      <ButtonGroup margins={false}>
        <Button transparent custom textColor={colors.text.brand} title="No, thanks" onPress={onCancel}/>
        <Button brand title={`Start ${defaultFreeTrialDays} day free trial`} onPress={onContinue}/>
      </ButtonGroup>
      <Spacer h={8}/>
      <Text TextThin-14 Secondary centered>{`Cancel anytime`}</Text>
    </View>);
};
const BenefitHeader = ({ benefitId, withMargin, hideIcon, }) => {
    const { name, minTier, text } = benefits[benefitId];
    const plansString = () => {
        let plansString = '';
        if (minTier === 'PLUS') {
            plansString = 'our premium plans.';
        }
        else if (minTier === 'PRO') {
            plansString = 'the Emma Pro and Ultimate plans.';
        }
        else if (minTier === 'ULTIMATE') {
            plansString = 'the Emma Ultimate plan.';
        }
        return plansString;
    };
    const defaultMessage = () => `${name} ${getVerbForm(name)} available on ${plansString()} ${text}.`;
    const featureStrings = {
        customCategories: {
            description: `${name} are available on ${plansString()} Get started creating customised budgets and tracking your spending your way.`,
        },
        export: {
            title: 'Export data',
            description: `Export data is available on ${plansString()} Easily export your full transaction history and account data in just a few taps.`,
        },
        offlineAccounts: {
            description: `${name} are available on ${plansString()} Start manually tracking accounts that aren’t supported.`,
        },
        smartRules: {
            description: `${name} are part of ${plansString()} Automate your transactions with custom rules that fit your budgeting style.`,
        },
        spaces: {
            description: `${name} are part of ${plansString()} Create custom spaces to organise your money the way you want.`,
        },
        budgeting: {
            title: 'Budgeting',
            description: `Budgeting is available on ${plansString()} Set monthly limits by category to stay in control of your spending.`,
        },
        cashback: {
            description: `${name} is available on ${plansString()} Earn money back when you shop with our partnered brands right from the app.`,
        },
        rentReporting: {
            title: 'Rent Reporting',
            description: `Rent Reporting is available on ${plansString()} Build your credit history with Emma Pro or Ultimate to report your rent payments to all the major credit agencies.`,
        },
        forceSync: {
            title: 'On-demand Sync',
            description: `Emma automatically updates your bank data up to 4 times a day. With Emma Pro or Ultimate, you can sync your accounts anytime, on demand.`,
        },
        connections: {
            description: `To add more bank connections you need to upgrade to Plus for 4, or Emma Pro or Ultimate for unlimited. Your plan currently supports 2 free bank connections. `,
        },
        business: {
            description: `${name} are part of ${plansString()} Unlock the ability to track your business finances and manage your expenses in one place.`,
        },
        split: {
            title: 'Split transaction',
            description: `Split transaction is available on ${plansString()} Split any transaction into multiple categories to keep your budget accurate and on track.`,
        },
        changeTransactionDate: {
            description: `${name} is available on ${plansString()} Edit transaction dates to match when you actually spent, for more accurate budgeting.`,
        },
        renameTransactions: {
            description: `${name} is available on ${plansString()} Rename transactions to make your spending clearer and easier to track.`,
        },
        advancedTransactionsEditing: {
            description: `${name} is available with ${plansString()} It lets you edit multiple transactions at once — a real time-saver! Don’t worry — you can still edit transactions one by one by tapping on them individually.`,
            openLink: links.openTransactionCategories,
        },
    };
    const { title: benefitTitle, description, openLink, } = featureStrings[benefitId] || {};
    const title = `Unlock ${benefitTitle || name}`;
    const subTitle = description || defaultMessage();
    return (<View style={[!!withMargin && styles.horizontalMargin, styles.headerContainer]}>
      {!hideIcon && <IconUnlocked width={32} height={32}/>}
      <Spacer h={12}/>
      <Text Text-20 centered>
        {title}
      </Text>
      <Spacer h={8}/>
      <Text TextThin-14 Secondary centered>
        {subTitle}{' '}
        {!!openLink && (<Text Text-14 Primary onPress={links.openTransactionCategories} suppressHighlighting>
            {`Learn\xa0more`}
          </Text>)}
      </Text>
    </View>);
};
const PremiumFeatureMicroModalContent = ({ benefitId, onContinue, onCancel, version, }) => (<View>
      <View style={styles.container}>
        <Spacer h={40}/>
        <RenderUnlockIcon />
        <Spacer h={16}/>
        <BenefitHeader benefitId={benefitId} hideIcon/>
        <Spacer h={16}/>
        {version === 'v2' && <UpSellCard />}
      </View>
      {version === 'v2' ? (<UpSellButtons onCancel={onCancel} onContinue={onContinue}/>) : (<ButtonGroup margins={false}>
          <Button brandReversed title="No, thanks" onPress={onCancel}/>
          <Button brand title="Continue" onPress={onContinue}/>
        </ButtonGroup>)}
    </View>);
const styles = StyleSheet.create({
    container: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    horizontalMargin: {
        marginHorizontal: rem(16),
    },
    headerContainer: {
        alignItems: 'center',
        marginTop: rem(-16),
    },
});
export default PremiumFeatureMicroModalContent;
export { RenderUnlockIcon, BenefitHeader };
